import http from 'common-module/src/util/http';
import dayjs from 'dayjs';
import { Message } from 'zk-ui';

/**
 * 获取操作记录列表
 * @param id 事项ID
 * @returns {Promise<*>}
 */
export const getActivityList = async id => {
  return http.post('/issue/activity-list', { issueId: id });
};

/**
 * 获取操作记录里工时登记列表
 * @param params 事项ID
 * @returns {Promise<*>}
 */
export async function getLabourRecordList(params) {
  return http.post('/labour-record/list', params);
}
/**
 * 编辑操作记录里工时登记
 * @param params 事项ID
 * @returns {Promise<*>}
 */
export async function editLabourRecord(params) {
  return http.post('/labour-record/update', params);
}
/**
 * 删除操作记录里工时登记
 * @param id 事项ID
 * @returns {Promise<*>}
 */
export async function deleteLabourRecord(id) {
  return await http.post('/labour-record/remove', id);
}

/**
 * 分页获取评论列表
 * @param id 事项id
 * @param pageSize 分页大小
 * @param pageNumber 页码
 * @param projectId 项目ID
 * @returns {Promise<*>}
 */
export const getCommentList = async (id, pageSize, pageNumber, projectId, timeType, boolean) => {
  return http.post('/sys-comment/issue/list-comment', { linkId: id, pageSize, pageNumber, projectId, queryOrderBy: { field: timeType, desc: boolean } });
};

/**
 * 获取事项附件列表
 * @param issueId
 * @returns {Promise<*>}
 */
export const getAttachmentList = async (issueId) => {
  const items = await http.post('sys-file/list/issue-attachment', { issueId });
  return items.map(i => ({
    id: i.fileKey,
    name: `${i.fileName}${i.fileSuffix}`,
    type: i.fileType,
    url: i.originalUrl || i.fileDownloadPath, // originalUrl
    time: dayjs(i.uploadTime).format('YYYY-MM-DD HH:mm'),
    size: i.fileSize,
    ...i
  }));
};

/**
 * 获取事项子任务列表
 * @param issueId
 * @returns {Promise<*>}
 */
export const getSubtaskList = async (issueId) => {
  const result = await http.post('/issue/subtask-list/issue-id', { issueId });
  return result.map(item => ({
    id: item.id,
    assignee: item.assignee,
    status: item.statusId,
    stateCategory: item.status.stateCategory,
    summary: item.summary,
    estimatePoint: item.estimatePoint,
    dueDate: item.dueDate
  }));
};

/**
 * 获取事项关联列表
 * @param issueId 事项ID
 * @returns {Promise<*>}
 */
export const getRelationList = async issueId => {
  const result = await http.post('/issue/relationship-list/issue-id', { issueId });
  return result.map(item => ({
    ...item,
    statusVO: item.status,
    status: item.status.id
  }));
};

/**
 * 删除附件
 * @param id 附件id
 * @returns {Promise<*>}
 */
export const removeAttachment = async (id, issueId, refId) => {
  return http.post('sys-file/delete', {
    fileKeys: [id],
    linkModule: 'issue',
    linkEntity: 'attachment',
    relIds: [refId],
    linkId: issueId
  });
};

/**
 * 回复评论
 * @param replyId 评论ID
 * @param content 内容
 * @param commentAttributes 额外内容
 * @returns {Promise<*>}
 */
export const replyComment = async ({ commentId, content, commentAttributes, files }) => {
  return http.post('/sys-comment/add-reply', { replyId: commentId, content, commentAttributes, files });
};

/**
 * 删除评论
 * @param commentId 评论ID
 * @returns {Promise<*>}
 */
export const deleteComment = async commentId => {
  return http.post('/sys-comment/delete', { commentId });
};

/**
 * 分页获取评论回复列表
 * @param groupId 评论ID
 * @param pageSize 分页大小
 * @param pageNumber 页码
 * @returns {Promise<*>}
 */
export const getCommentReplyList = async (groupId, linkId, pageSize, pageNumber) => {
  return http.post('/sys-comment/issue/list-reply', { groupId, linkId, pageSize, pageNumber });
};

/**
 * 评论点赞与取消
 * @param commentId 评论ID
 * @param hasLike 是否喜欢
 * @returns {Promise<*>}
 */
export const likeToggleComment = async (commentId, hasLike) => {
  if (hasLike) {
    return http.post('/sys-favour/issue-comment/recover', { commentId });
  }
  return http.post('/sys-favour/issue-comment/up', { commentId });
};

/**
 * 更新表情
 * @param data 更新表情数据
 * @returns {Promise<*>}
 */
export const stickerUpdate = async (data) => {
  return http.post('/sys-sticker/update', data);
};

/**
 * 修改评论内容
 * @param commentId 评论ID
 * @param content 内容
 * @param commentAttributes 额外信息
 * @returns {Promise<*>}
 */
export const updateComment = async ({ commentId, content, commentAttributes, files }) => {
  return http.post('/sys-comment/update', { commentId, content, commentAttributes, files });
};

/**
 * 创建评论
 * @param issueId 事项ID
 * @param content 内容
 * @param commentAttributes 额外内容
 * @returns {Promise<*>}
 */
export const createComment = async (issueId, content, commentAttributes, files) => {
  return http.post('/sys-comment/issue/add-comment', { content, linkId: issueId, commentAttributes, files });
};

/**
 * 删除事项
 * @param issueId 事项ID
 * @returns {Promise<*>}
 */
export async function deleteIssue(issueId) {
  return http.post('/issue/delete', { issueId });
}

/**
 * 复制事项
 * @param issueId 事项ID
 * @returns {Promise<*>}
 */
export async function duplicateIssue(issueId) {
  return http.post('/issue/duplicate', { issueId });
}

/**
 * 获取设置点数的选项列表
 * @param projectId 项目ID
 * @returns {Promise<*>}
 */
export const getEstimatePointOption = async (projectId) => {
  return http.post('/issue/estimate-point-option', { projectId });
};

/**
 * 删除子任务
 * @param issueId 事项ID
 * @param subTaskId 子任务ID
 * @returns {Promise<*>}
 */
export const removeSubtask = async (issueId, subTaskId) => {
  return http.post('/issue/remove-subtask', { issueId, subTaskId });
};

/**
 * 更新子任务
 * @param data 子任务数据
 * @returns {Promise<*>}
 */
export const updateSubtask = async (data) => {
  // return http.post('/issue/update-v2', data);
  return http.post('/issue/update', data);
};
/**
 * 新建子任务
 * @param data 子任务数据
 * @returns {Promise<*>}
 */
export const addSubtask = async (data) => {
  return http.post('/issue/add-subtask', data);
};

/**
 * 获取子任务状态
 * @param projectId 项目ID
 * @returns {Promise<*>}
 */
export const getSubtaskStatus = async (projectId) => {
  return http.post('/issue/subtask-status', { projectId });
};

/**
 * 创建标签
 * @param labelInfo
 * @returns {Promise<*>}
 */
export const createLabel = async (labelInfo) => {
  return http.post('/sys-label/add', labelInfo);
};

/**
 * 获取工作流信息
 * @param id 工作流ID
 * @returns {Promise<*>}
 */
export const getWorkFlowInfo = async id => {
  return http.post('/workflow/get/layout', { id });
};

/**
 * 加载事项数据
 * @param issueId
 * @returns {Promise<*>}
 */
export const loadIssue = async (issueId) => {
  return http.post('/issue/load', { issueId });
};

/**
 * 记录指引已完成
 * @param guideType 指引类型
 * @returns {Promise<*>}
 */
export const guideRecord = async (guideType) => {
  return http.post('/account/guide-record', { guideType });
};

/**
 * 加载事项类型列表
 * @param projectId 项目ID
 * @returns {Promise<*>}
 */
export const loadIssueTypes = async projectId => {
  return http.post('/issue-type/list-selector/project-id', { projectId });
};

/**
 * 加载事项布局
 * @param projectId 项目ID
 * @param issueTypeId 事项类型ID
 * @returns {Promise<*>}
 */
export const loadIssueLayout = async (projectId, issueTypeId) => {
  return http.post('/issue/load-layout', { projectId, issueTypeId });
};

/**
 * 加载事项布局字段选项
 * @param projectId 项目ID
 * @param issueTypeId 事项类型ID
 * @returns {Promise<*>}
 */
export const loadIssueLayoutFieldSelectOptions = async (projectId, issueTypeId) => {
  return http.post('/issue/select-options', { projectId, issueTypeId });
};

/**
 * 创建事项
 * @param issue
 * @returns {Promise<*>}
 */
export const createIssue = async (issue) => {
  return http.post('/issue/add', issue);
};

/**
 * 批量创建事项
 * @param issue
 * @returns {Promise<*>}
 */
export const batchCreateIssue = async (data) => http.post('/issue/batch-quick-add', data);

/**
 * 更新事项
 * @param id 事项ID
 * @param changes 更新内容
 * @returns {Promise<*>}
 */
export const updateIssue = async (id, changes) => {
  // return http.post('/issue/update-v2', { id, data: changes });
  return http.post('/issue/update', { id, data: changes });
};

/**
 * 分页查询事项列表
 * @param projectId 项目ID
 * @param queryKeyword 关键字
 * @param notInIssueIds 忽略事项ID列表
 * @param pageSize 分页大小
 * @param pageNumber 页码
 * @returns {Promise<*>}
 */
// export const searchIssue = async (data) => {
//   return http.post('/issue/list-selector', data);
// };
export const searchIssue = async (data, config) => {
  return http.post('/issue/query-issue-for-relationship', data, config);
};

/**
 * 添加事项关联
 * @param issueId 事项ID
 * @param destinationIssueIdList 目标关联ID数组
 * @param linkType 类型
 * @returns {Promise<*>}
 */
export const addRelation = async (issueId, destinationIssueIdList, linkType) => {
  try {
    const res = await http.post('/issue/add-issue-relationship', { issueId, destinationIssueIdList, linkType }, { showError: false });
    return res;
  } catch (err) {
    if (err.message) {
      Message.error({
        message: err.message, dangerouslyUseHTMLString: true
      });
    }
    throw err;
  }
};

/**
 * 更新事项关联
 * @param issueId 事项ID
 * @param relationshipId 关联ID
 * @param destinationIssueId 目标关联ID
 * @param linkType 类型
 * @returns {Promise<*>}
 */
export const updateRelation = async (issueId, relationshipId, destinationIssueId, linkType) => {
  return http.post('/issue/update-issue-relationship', { issueId, relationshipId, destinationIssueId, linkType });
};


/**
 * 删除事项关联
 * @param issueId 事项ID
 * @param relationshipId 关联ID
 * @returns {Promise<*>}
 */
export const removeRelation = async (issueId, relationshipId) => {
  return http.post('/issue/remove-issue-relationship', { issueId, relationshipId });
};

/**
 * 获取事项关联筛选选项列表
 * @param projectId 项目ID
*/
export const getRelationshipOptions = async (projectId) => {
  return http.post('/issue/query-relationship-options', { projectId });
};

/**
 * 关联用户行为上报
*/
export const userReportRelationship = async (data) => {
  return http.post(`/relationship/${data.issueId}/search`, data, { showError: false });
};

/**
 * 计算公式字段关联字段变动后表达式的值
 * @param data
 * @returns {Promise<*>}
 */
export async function expressionFieldChange(data) {
  return http.post('/issue/get/expression-field-change-value', data);
}

/**
 * issue详情聚合接口
 * @param data
 * @returns {Promise<*>}
*/
export const issueDialogDetails = async (data) => {
  return http.post('/issue/issue-assemble-info', data, { showError: false });
};

/**
 * 获取看板及看板列信息,传项目id和问题类型id
 * @param data
 * @returns {Promise<*>}
 */
export const kanbanColumnInfo = async (data) => {
  return http.post('/kanban/column-info', data, { showError: false });
};

/**
 * 获取 @ 的用户下拉列表数据
 * @param projectId
 * @returns {Promise<*>}
*/
export const getMemberList = async (projectId) => {
  return http.post('/member/project-users', { projectId, containAccUnEnabled: true });
};

export const getRecentlyList = async (projectId, notInIssueIds) => {
  const params = { projectId };
  if (notInIssueIds?.length) {
    params.notInIssueIds = notInIssueIds;
  }
  return http.post('/issue/recently-issue-list', params);
};

export const getRecentlyListForRelation = async (
  { projectId, notInIssueIds, inIssueIds, issueId, issueTypeId, summary, linkType },
  config = {}
) => {
  return http.post('/issue/recommend-issue-list-for-relation', {
    projectId,
    issueId,
    issueTypeId,
    summary,
    linkType,
    notInIssueIds: notInIssueIds?.length ? notInIssueIds : undefined,
    inIssueIds: inIssueIds?.length ? inIssueIds : undefined
  }, config);
};

/**
 * 获取指派人推荐列表
 * @param data
 * @returns {Promise<*>}
 */
export const getAssigneeRecommendList = async data => {
  return http.post('/issue/assignee-recommend', data, { showError: false });
};
/**
 * 获取指派人推荐列表-v2
 * @param data
 * @returns {Promise<*>}
 */
export const getAssigneeRecommendListV2 = async data => {
  return http.post('/issue/assignee-recommend-v2', data, { showError: false });
};
/**
 * 获取指派人推荐列表的点数
 * @param data
 * @returns {Promise<*>}
 */
export const getAssigneeRecommendListPoint = async data => {
  return http.post('/issue/query-assignees-estimate-point-v2', data, { showError: false });
};
/**
 * 获取项目成员在项目中的信息
 * @param projectId 项目ID
 * @returns {Promise<*>}
 */
export const loadMemberProjectInfo = async projectId => {
  return http.post('/member/project-user-info', { projectId });
};

/**
 *
 * @param {*} data {	"done": true, "id": 0, "projectId": 0}
 * @returns
 */
export const updateSubtaskStatus = async data => {
  return http.post('/issue/update-subtask-status', data);
};

/**
 * 获取事项构建列表
 * @param {*} data
 * @returns
 */
export const getJenkinsIssueTaskList = async issueId => {
  return http.get('/jenkins/query-task-list-by-issue-id', { issueId });
};

/**
 * 更新附件内容
 * @param {*} fileKey 附件fileKey
 * @param {*} body 附件内容
 * @returns
 */
export const updateAttachmentContent = async (fileKey, body) => {
  return http.post('sys-file/update-file-object', { fileKey, body });
};

/**
 * 下载附件内容
 * @param {*} fileKey 附件fileKey
 * @returns
 */
export const downloadAttachmentContent = async fileKey => {
  const response = await http.post('sys-file/download', { fileKey });
  return response;
};
